// Component: ThumbnailUploader
// Purpose: Upload or auto-generate a thumbnail image from a provided video file
// or URL. Provides drag-and-drop, preview, and small status helpers.
import React, { useState, useRef, useEffect } from 'react';
import { Video, Image, Wand2, AlertCircle } from 'lucide-react';

const ThumbnailUploader = ({ 
  onThumbnailChange, 
  error, 
  initialThumbnail, 
  existingThumbnail,
  videoFile, // New prop: the selected video file
  videoUrl   // New prop: video URL for generating thumbnail
}) => {
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState(initialThumbnail?.name || existingThumbnail?.name || '');
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(null);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [thumbnailSource, setThumbnailSource] = useState('none'); // 'none', 'uploaded', 'generated'
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Set initial preview from prop if available
  useEffect(() => {
    // Handle both initialThumbnail and existingThumbnail for backward compatibility
    const thumbnailSourceUrl = initialThumbnail?.url || existingThumbnail?.url;
    if (thumbnailSourceUrl) {
      setPreview(thumbnailSourceUrl);
      setThumbnailSource('uploaded');
      
      // If no file is selected yet but we have an initial thumbnail, we need to tell the parent
      // that we're using this thumbnail (for validation purposes)
      const thumbnailData = {
        isExistingThumbnail: true,
        url: thumbnailSourceUrl,
        name: initialThumbnail?.name || existingThumbnail?.name || 'Existing Thumbnail'
      };
      
      if (typeof onThumbnailChange === 'function') {
        onThumbnailChange(thumbnailData);
      }
    }
  }, [initialThumbnail, existingThumbnail, onThumbnailChange]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    // Validate file type
    if (!file.type.match('image.*')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert('Image file size must be less than 5MB');
      return;
    }

    setFileName(file.name);
    setThumbnailSource('uploaded');
    setGeneratedThumbnail(null); // Clear any generated thumbnail
    setThumbnailError(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // Call the parent callback
    if (typeof onThumbnailChange === 'function') {
      onThumbnailChange(file);
    }
  };

  const generateThumbnailFromVideo = async () => {
    if (!videoFile && !videoUrl) {
      setThumbnailError('No video available to generate thumbnail from');
      return;
    }

    setIsGeneratingThumbnail(true);
    setThumbnailError(null);

    try {
      // Create video element to load the video and extract a frame
      const video = document.createElement('video');
      videoRef.current = video;
      
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      // Create a promise to handle the thumbnail generation
      const thumbnailPromise = new Promise((resolve, reject) => {
        // Set up event listeners for extracting thumbnail
        video.onloadeddata = () => {
          try {
            // Seek to 0.5 seconds (or 25% of the duration if very short)
            const seekTime = Math.min(video.duration * 0.25, 0.5);
            video.currentTime = seekTime;
          } catch (err) {
            console.error('Error seeking video:', err);
            reject(new Error('Failed to seek video'));
          }
        };
        
        video.onseeked = () => {
          try {
            // Create a canvas to draw the video frame
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            // Draw the video frame to the canvas
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Convert canvas to data URL
            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            // Convert data URL to blob for file handling
            canvas.toBlob((blob) => {
              if (blob) {
                // Create a file-like object from the blob
                const thumbnailFile = new File([blob], 'video_thumbnail.jpg', {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                resolve({
                  dataUrl: thumbnailDataUrl,
                  file: thumbnailFile
                });
              } else {
                reject(new Error('Failed to create thumbnail blob'));
              }
            }, 'image/jpeg', 0.8);
            
            // Clean up
            video.pause();
            if (video.src.startsWith('blob:')) {
              URL.revokeObjectURL(video.src);
            }
            videoRef.current = null;
          } catch (err) {
            console.error('Error generating thumbnail:', err);
            reject(new Error('Failed to generate thumbnail'));
          }
        };
        
        video.onerror = () => {
          console.error('Error loading video for thumbnail');
          reject(new Error('Failed to load video'));
          if (video.src.startsWith('blob:')) {
            URL.revokeObjectURL(video.src);
          }
          videoRef.current = null;
        };
      });

      // Set video source and start loading
      if (videoFile) {
        // Use the file directly (blob URL)
        const url = URL.createObjectURL(videoFile);
        video.src = url;
      } else if (videoUrl) {
        // Use the provided video URL
        video.src = videoUrl;
      }
      
      video.load();

      // Wait for thumbnail generation
      const result = await thumbnailPromise;
      
      // Update state with generated thumbnail
      setGeneratedThumbnail(result.dataUrl);
      setPreview(result.dataUrl);
      setFileName('Generated from video');
      setThumbnailSource('generated');
      
      // Call the parent callback with the generated thumbnail file
      if (typeof onThumbnailChange === 'function') {
        onThumbnailChange(result.file);
      }

    } catch (error) {
      console.error('Error generating thumbnail:', error);
      setThumbnailError(error.message || 'Failed to generate thumbnail from video');
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveThumbnail = (e) => {
    e.stopPropagation();
    setPreview(null);
    setFileName('');
    setGeneratedThumbnail(null);
    setThumbnailSource('none');
    setThumbnailError(null);
    
    // Call the parent callback with null to remove the thumbnail
    if (typeof onThumbnailChange === 'function') {
      onThumbnailChange(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cleanup video ref on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        if (videoRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(videoRef.current.src);
        }
        videoRef.current = null;
      }
    };
  }, []);

  const canGenerateThumbnail = (videoFile || videoUrl) && !isGeneratingThumbnail;

  return (
    <div className="space-y-3">
      {/* Action Buttons */}
      {canGenerateThumbnail && !preview && (
        <div className="flex gap-2 justify-center">
          <button
            type="button"
            onClick={generateThumbnailFromVideo}
            disabled={isGeneratingThumbnail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
          >
            {isGeneratingThumbnail ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Generate from Video
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleClick}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            <Image className="w-4 h-4" />
            Upload Custom
          </button>
        </div>
      )}

      {/* Main Upload Area */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : error
            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-300 hover:border-primary-500 dark:border-gray-600 dark:hover:border-primary-500'
        }`}
      >
        {preview ? (
          <div className="relative">
            <div className="absolute top-0 right-0 p-1 z-10">
              <button
                onClick={handleRemoveThumbnail}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-colors"
                title="Remove thumbnail"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img
              src={preview}
              alt="Thumbnail Preview"
              className="max-h-64 mx-auto object-contain rounded"
            />
            <div className="mt-2 flex items-center justify-center gap-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">
                {fileName || 'Current Thumbnail'}
              </p>
              {thumbnailSource === 'generated' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs">
                  <Wand2 className="w-3 h-3" />
                  Auto-generated
                </span>
              )}
              {thumbnailSource === 'uploaded' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs">
                  <Image className="w-3 h-3" />
                  Custom
                </span>
              )}
            </div>
            
            {/* Action buttons when thumbnail exists */}
            {canGenerateThumbnail && (
              <div className="mt-3 flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    generateThumbnailFromVideo();
                  }}
                  disabled={isGeneratingThumbnail}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded text-sm transition-colors"
                >
                  {isGeneratingThumbnail ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-3 h-3" />
                      Regenerate
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  className="flex items-center gap-1 px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors"
                >
                  <Image className="w-3 h-3" />
                  Replace
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8">
            <div className="flex justify-center mb-4">
              {canGenerateThumbnail ? (
                <Video className="h-12 w-12 text-blue-400 dark:text-blue-500" />
              ) : (
                <svg
                  className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            {canGenerateThumbnail ? (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Generate thumbnail from video or upload custom image
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Thumbnail is optional - will auto-generate if not provided
                </p>
              </div>
            ) : (
              <div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Click to upload or drag and drop
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  PNG, JPG, GIF up to 5MB (Optional)
                </p>
              </div>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      
      {/* Error Display */}
      {(error || thumbnailError) && !preview && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">
            {error || thumbnailError}
          </p>
        </div>
      )}
      
      {/* Info Message */}
      {!error && !thumbnailError && !preview && canGenerateThumbnail && (
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            💡 Tip: Auto-generate a thumbnail from your video or upload a custom image for better presentation
          </p>
        </div>
      )}
    </div>
  );
};

export default ThumbnailUploader; 